import React, { ReactNode, useRef, useState } from 'react'
import { useEventListener } from '../hooks/useEventListener'
import { cn } from '../utils/addToClassName'
import { Link } from '../utils/link'
import { slugify } from '../utils/slugify'
import { SVGIcon } from '../utils/svg'

export type NavItems = Array<ParentNavItem>
export type ParentNavItem = NavItem & { subNav?: SubNavItem }
export type SubNavItem = Array<NavItem>
export type NavItem = { label: string; url?: string; external: boolean }

export const Nav = ({
  children,
  id,
  navClassName,
  navListClassName,
  ariaLabel,
  menuOpen
}: {
  id?: string
  children: ReactNode
  navClassName?: string
  navListClassName?: string
  ariaLabel: string,
  menuOpen: boolean
}) => (
  <nav id={id} className={cn('nav', navClassName)} aria-label={ariaLabel} aria-hidden={!menuOpen}>
    <div className='nav__inner'>
      {children}
    </div>
  </nav>
)

export const NavItem = ({
  itemClassName,
  linkClassName,
  onClick,
  label,
  url,
  external,
  isActive = window.location.pathname == url,
  subNav,
}: {
  itemClassName?: string
  linkClassName?: string
  onClick?: () => void
  label: string
  url?: string
  external: boolean
  isActive?: boolean
  subNav?: Array<{ label: string; url?: string; external: boolean; isActive?: boolean }>
}) => {
  const menuItem = useRef<HTMLLIElement>(null)
  const [subIsOpen, setSubOpen] = useState(false)
  const hasDropdown = subNav && subNav.length > 0

  // Close dropdown on Escape
  useEventListener(
    'keydown',
    (e): void => {
      if (e.key === 'Escape') setSubOpen(false)
    },
    { passive: true },
    subIsOpen
  )
  return (
    <li
      ref={menuItem}
      className={cn(
        'nav__item',
        hasDropdown ? 'nav__item--dropdown' : '',
        hasDropdown && url ? 'nav__item--dropdown-has-link' : '',
        itemClassName
      )}
      onBlur={() => {
        // Close dropdown on blur
        if (subNav && subIsOpen && !menuItem.current?.matches(':focus-within')) setSubOpen(false)
      }}
    >
      {url && (
        <div className='nav__itemwrap'>
          <Link
            aria-current={isActive ? 'page' : undefined}
            className={cn('nav__link', linkClassName)}
            external={external}
            onClick={onClick}
            url={url}
          >
            <span className='nav__label'>{label}</span>
          </Link>
        </div>
      )}

      {/* Dropdown */}
      {hasDropdown && (
        <NavItemDropdownLayout
          label={label}
          items={subNav}
          open={subIsOpen}
          hasURL={url != undefined}
          onToggle={() => setSubOpen((prev) => !prev)}
          onClick={() => {
            setSubOpen(false)
            if (onClick) onClick()
          }}
        />
      )}
    </li>
  )
}

const NavItemDropdownLayout = ({
  label,
  hasURL,
  onClick,
  onToggle,
  items,
  open,
}: {
  label: string
  hasURL: boolean
  onToggle: () => void
  onClick?: () => void
  items: Array<{ label: string; url?: string; external?: boolean; isActive?: boolean }>
  open: boolean
}) => {
  const id = `a11y-dropdown-${slugify(label)}`
  const title = hasURL ? `${open ? 'Sluit' : 'Open'} ${label}` : label
  return (
    <>
      <button
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={id}
        title={title}
        className={cn('nav__button', !hasURL ? 'nav__link' : '')}
        onClick={onToggle}
      >
        <span className={hasURL ? 'sr-text' : ''}>{title}</span>
        <SVGIcon id="icon-chevron-down" className="nav__chevron" size={16} />
      </button>

      <div id={id} hidden={!open} className="nav__dropdown">
        <div className="nav__dropdown-inner">
          <ul className="nav__dropdown-list text--md">
            {items.map((child) => (
              <li key={child.label} className="nav__dropdown-item">
                {child.url ? (
                  <Link
                    aria-current={child.isActive ? 'page' : undefined}
                    className="nav__dropdown-link text--md"
                    external={child.external || false}
                    onClick={onClick}
                    url={child.url}
                  >
                    <span className='nav__dropdown-label'>{child.label}</span>
                  </Link>
                ) : (
                  <span className='nav__dropdown-label'>{child.label}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  )
}

const SubmenuLabel = ({ label }: { label: string }) => <span>{label}</span>
